/**
 * PortalInsights — client-facing analytics dashboard.
 *
 * Shows the client what's been happening at their facilities:
 *   - KPIs (last 12 months by default)
 *   - Monthly timeline of captures, bait consumption, UV insect counts
 *   - Pest type distributions
 *   - UV species breakdown + monthly insect count
 *
 * The heatmap visualization lives on /portal/mapas (PortalMaps.jsx).
 */
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useBranch } from "@/contexts/BranchContext";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import {
    ResponsiveContainer,
    LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, Legend,
    BarChart, Bar,
    PieChart, Pie, Cell,
    AreaChart, Area,
} from "recharts";
import { Box, Rat, Lightbulb, Map as MapIcon, Wrench, Activity, Calendar, TrendingUp, FileText, Sparkles, ArrowUpRight, ArrowDownRight, Minus } from "lucide-react";
import { format, parse } from "date-fns";
import { es } from "date-fns/locale";
import ExecutiveReportDialog from "@/components/ExecutiveReportDialog";
import ExecutiveReportsList from "@/components/ExecutiveReportsList";

const COLORS = {
    green: "#10b981",
    yellow: "#f59e0b",
    red: "#ef4444",
    navy: "#1e3a8a",
    purple: "#7c3aed",
    cyan: "#06b6d4",
    pink: "#ec4899",
    slate: "#64748b",
};

const PIE_PALETTE = ["#1e3a8a", "#ef4444", "#f59e0b", "#10b981", "#7c3aed", "#06b6d4", "#ec4899", "#64748b", "#84cc16", "#f97316"];

const fmtMonth = (m) => {
    if (!m) return "";
    try {
        return format(parse(m, "yyyy-MM", new Date()), "MMM yy", { locale: es });
    } catch {
        return m;
    }
};

function DeltaBadge({ pct, higherIsBad = true }) {
    if (pct === null || pct === undefined) {
        return <span className="text-[10px] mt-1 inline-block bg-white/15 px-1.5 py-0.5 rounded-full">Sin histórico previo</span>;
    }
    if (pct === 0) {
        return (
            <span className="text-[10px] mt-1 inline-flex items-center gap-0.5 bg-white/15 px-1.5 py-0.5 rounded-full">
                <Minus className="w-2.5 h-2.5" /> sin cambio
            </span>
        );
    }
    const up = pct > 0;
    const bad = higherIsBad ? up : !up;
    const cls = bad ? "bg-red-500/80" : "bg-emerald-500/80";
    const Icon = up ? ArrowUpRight : ArrowDownRight;
    return (
        <span className={`text-[10px] mt-1 inline-flex items-center gap-0.5 ${cls} px-1.5 py-0.5 rounded-full font-semibold`}>
            <Icon className="w-2.5 h-2.5" /> {Math.abs(pct)}% vs periodo anterior
        </span>
    );
}

function KpiCard({ icon: Icon, label, value, sub, color = "navy", testId, deltaPct, higherIsBad = true }) {
    const tone = {
        navy: "from-indigo-900 to-slate-900 text-white",
        red: "from-rose-600 to-red-700 text-white",
        amber: "from-amber-500 to-orange-600 text-white",
        emerald: "from-emerald-500 to-teal-700 text-white",
        purple: "from-violet-600 to-fuchsia-700 text-white",
    }[color];
    return (
        <div className={`rounded-2xl p-4 bg-gradient-to-br ${tone} relative overflow-hidden`} data-testid={testId}>
            <div className="absolute -right-6 -top-6 w-24 h-24 rounded-full bg-white/10" />
            <div className="flex items-center gap-2 text-xs uppercase tracking-widest opacity-80">
                <Icon className="w-3.5 h-3.5" /> {label}
            </div>
            <div className="text-3xl font-bold mt-1" style={{ fontFamily: "'Space Grotesk',sans-serif" }}>{value}</div>
            {sub ? <div className="text-xs opacity-80 mt-0.5">{sub}</div> : null}
            <div><DeltaBadge pct={deltaPct} higherIsBad={higherIsBad} /></div>
        </div>
    );
}

export default function PortalInsights() {
    const { branchParam } = useBranch();
    const [months, setMonths] = useState(12);
    const [summary, setSummary] = useState(null);
    const [timeline, setTimeline] = useState(null);
    const [pests, setPests] = useState(null);
    const [uv, setUv] = useState(null);
    const [comparison, setComparison] = useState(null);
    const [reports, setReports] = useState([]);
    const [floorPlans, setFloorPlans] = useState([]);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [loading, setLoading] = useState(true);

    const reloadReports = async () => {
        try {
            const { data } = await api.get("/executive-reports");
            setReports(data || []);
        } catch { /* ignore */ }
    };

    useEffect(() => {
        let alive = true;
        setLoading(true);
        (async () => {
            try {
                const bp = branchParam;
                const [s, t, p, u, c, fp, rep] = await Promise.all([
                    api.get("/portal/insights/summary", { params: { months, ...bp } }),
                    api.get("/portal/insights/timeline", { params: { months, ...bp } }),
                    api.get("/portal/insights/pests", { params: { months, ...bp } }),
                    api.get("/portal/insights/uv", { params: { months, ...bp } }),
                    api.get("/portal/insights/comparison", { params: { months, ...bp } }),
                    api.get("/floor-plans", { params: bp }),
                    api.get("/executive-reports"),
                ]);
                if (!alive) return;
                setSummary(s.data);
                setTimeline(t.data);
                setPests(p.data);
                setUv(u.data);
                setComparison(c.data);
                setFloorPlans(fp.data || []);
                setReports(rep.data || []);
            } finally {
                if (alive) setLoading(false);
            }
        })();
        return () => { alive = false; };
    }, [months, branchParam]);

    const deltas = comparison?.delta_pct || {};

    const timelineData = useMemo(() => (
        (timeline?.series || []).map((r) => ({ ...r, monthLabel: fmtMonth(r.month) }))
    ), [timeline]);

    const uvMonthly = useMemo(() => (
        (uv?.monthly || []).map((r) => ({ ...r, monthLabel: fmtMonth(r.month) }))
    ), [uv]);

    const zoneData = useMemo(() => {
        const z = summary?.devices_by_zone || {};
        return [
            { key: "verde", label: "Verde (cebos exterior)", count: z.verde || 0, fill: COLORS.green },
            { key: "amarilla", label: "Amarilla (cebos perímetro)", count: z.amarilla || 0, fill: COLORS.yellow },
            { key: "roja", label: "Roja (trampas + UV interior)", count: z.roja || 0, fill: COLORS.red },
        ].filter((x) => x.count > 0);
    }, [summary]);

    return (
        <div data-testid="portal-insights">
            {/* Header */}
            <div className="flex items-start justify-between mb-6 gap-4 flex-wrap">
                <div>
                    <h1 className="text-3xl font-semibold mb-1" style={{ fontFamily: "'Space Grotesk',sans-serif" }}>
                        Estadísticas de servicio
                    </h1>
                    <p className="text-muted-foreground text-sm">
                        Visibilidad de capturas, consumos y actividad de plagas en tus instalaciones.
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    <span className="text-xs text-muted-foreground hidden sm:inline">Periodo</span>
                    <Select value={String(months)} onValueChange={(v) => setMonths(parseInt(v, 10))}>
                        <SelectTrigger className="w-[140px]" data-testid="insights-months-select">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="3">Últimos 3 meses</SelectItem>
                            <SelectItem value="6">Últimos 6 meses</SelectItem>
                            <SelectItem value="12">Últimos 12 meses</SelectItem>
                            <SelectItem value="24">Últimos 24 meses</SelectItem>
                        </SelectContent>
                    </Select>
                    <Link to="/portal/mapas">
                        <Button variant="outline" data-testid="go-to-maps-button">
                            <MapIcon className="w-4 h-4 mr-1.5" /> Ver mapas
                        </Button>
                    </Link>
                    <Button onClick={() => setDialogOpen(true)} data-testid="open-exec-report-dialog"
                            className="bg-amber-500 text-black hover:bg-amber-400">
                        <Sparkles className="w-4 h-4 mr-1.5" /> Generar informe ejecutivo
                    </Button>
                </div>
            </div>

            {/* KPIs */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
                <KpiCard icon={Rat} label="Capturas roedores" value={summary?.totals.captures ?? "—"}
                         sub={`en ${months} meses`} color="red" testId="kpi-captures"
                         deltaPct={deltas.captures} higherIsBad={true} />
                <KpiCard icon={Box} label="Cebos consumidos" value={summary?.totals.consumed_baits ?? "—"}
                         sub="zonas verde + amarilla" color="amber" testId="kpi-consumed"
                         deltaPct={deltas.consumed} higherIsBad={true} />
                <KpiCard icon={Lightbulb} label="Insectos UV" value={summary?.totals.uv_insects ?? "—"}
                         sub="suma de lámparas" color="purple" testId="kpi-uv"
                         deltaPct={deltas.uv_insects} higherIsBad={true} />
                <KpiCard icon={Calendar} label="Visitas" value={summary?.totals.services ?? "—"}
                         sub="servicios realizados" color="navy" testId="kpi-services"
                         deltaPct={deltas.services} higherIsBad={false} />
                <KpiCard icon={Wrench} label="Dispositivos" value={summary?.totals.devices ?? "—"}
                         sub="activos en planta" color="emerald" testId="kpi-devices" />
            </div>

            {/* Tabs */}
            <Tabs defaultValue="overview" className="w-full">
                <TabsList className="mb-4 flex-wrap" data-testid="insights-tabs">
                    <TabsTrigger value="overview" data-testid="tab-overview">
                        <TrendingUp className="w-3.5 h-3.5 mr-1.5" /> Vista general
                    </TabsTrigger>
                    <TabsTrigger value="rodents" data-testid="tab-rodents">
                        <Rat className="w-3.5 h-3.5 mr-1.5" /> Roedores
                    </TabsTrigger>
                    <TabsTrigger value="baits" data-testid="tab-baits">
                        <Box className="w-3.5 h-3.5 mr-1.5" /> Cebos
                    </TabsTrigger>
                    <TabsTrigger value="uv" data-testid="tab-uv">
                        <Lightbulb className="w-3.5 h-3.5 mr-1.5" /> Lámparas UV
                    </TabsTrigger>
                    <TabsTrigger value="reports" data-testid="tab-reports">
                        <FileText className="w-3.5 h-3.5 mr-1.5" /> Informes generados
                        {reports.length > 0 && (
                            <span className="ml-1.5 bg-indigo-900 text-white text-[9px] rounded-full px-1.5 py-0.5 font-bold">
                                {reports.length}
                            </span>
                        )}
                    </TabsTrigger>
                </TabsList>

                {/* OVERVIEW */}
                <TabsContent value="overview">
                    <div className="grid lg:grid-cols-3 gap-4">
                        <Card className="lg:col-span-2">
                            <CardHeader>
                                <CardTitle className="text-base flex items-center gap-2">
                                    <Activity className="w-4 h-4 text-indigo-600" />
                                    Actividad mensual consolidada
                                </CardTitle>
                            </CardHeader>
                            <CardContent>
                                {loading ? <SkeletonChart /> : (
                                    <ResponsiveContainer width="100%" height={320}>
                                        <AreaChart data={timelineData}>
                                            <defs>
                                                <linearGradient id="cap" x1="0" y1="0" x2="0" y2="1">
                                                    <stop offset="5%" stopColor={COLORS.red} stopOpacity={0.5}/>
                                                    <stop offset="95%" stopColor={COLORS.red} stopOpacity={0.05}/>
                                                </linearGradient>
                                                <linearGradient id="con" x1="0" y1="0" x2="0" y2="1">
                                                    <stop offset="5%" stopColor={COLORS.yellow} stopOpacity={0.5}/>
                                                    <stop offset="95%" stopColor={COLORS.yellow} stopOpacity={0.05}/>
                                                </linearGradient>
                                                <linearGradient id="uvc" x1="0" y1="0" x2="0" y2="1">
                                                    <stop offset="5%" stopColor={COLORS.purple} stopOpacity={0.5}/>
                                                    <stop offset="95%" stopColor={COLORS.purple} stopOpacity={0.05}/>
                                                </linearGradient>
                                            </defs>
                                            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                                            <XAxis dataKey="monthLabel" tick={{ fontSize: 11 }} />
                                            <YAxis tick={{ fontSize: 11 }} />
                                            <Tooltip />
                                            <Legend wrapperStyle={{ fontSize: 12 }} />
                                            <Area type="monotone" dataKey="captures_total" name="Capturas" stroke={COLORS.red} fill="url(#cap)" />
                                            <Area type="monotone" dataKey="consumed_total" name="Cebos consumidos" stroke={COLORS.yellow} fill="url(#con)" />
                                            <Area type="monotone" dataKey="uv_insects" name="Insectos UV" stroke={COLORS.purple} fill="url(#uvc)" />
                                        </AreaChart>
                                    </ResponsiveContainer>
                                )}
                            </CardContent>
                        </Card>

                        <Card>
                            <CardHeader>
                                <CardTitle className="text-base">Dispositivos por perímetro</CardTitle>
                            </CardHeader>
                            <CardContent>
                                {zoneData.length === 0 ? <Empty /> : (
                                    <ResponsiveContainer width="100%" height={260}>
                                        <PieChart>
                                            <Pie data={zoneData} dataKey="count" nameKey="label" outerRadius={90} label>
                                                {zoneData.map((d, i) => <Cell key={i} fill={d.fill} />)}
                                            </Pie>
                                            <Tooltip />
                                        </PieChart>
                                    </ResponsiveContainer>
                                )}
                                <div className="text-[11px] text-muted-foreground mt-2 leading-relaxed">
                                    <b>Verde:</b> cebaderos en perímetro exterior.&nbsp;
                                    <b>Amarilla:</b> cebaderos perimetrales del edificio.&nbsp;
                                    <b>Roja:</b> trampas y lámparas en interior.
                                </div>
                            </CardContent>
                        </Card>
                    </div>
                </TabsContent>

                {/* RODENTS */}
                <TabsContent value="rodents">
                    <div className="grid lg:grid-cols-3 gap-4">
                        <Card className="lg:col-span-2">
                            <CardHeader>
                                <CardTitle className="text-base">Capturas mensuales por especie</CardTitle>
                            </CardHeader>
                            <CardContent>
                                {loading ? <SkeletonChart /> : (
                                    <ResponsiveContainer width="100%" height={320}>
                                        <BarChart data={timelineData}>
                                            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                                            <XAxis dataKey="monthLabel" tick={{ fontSize: 11 }} />
                                            <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                                            <Tooltip />
                                            <Legend wrapperStyle={{ fontSize: 12 }} />
                                            <Bar dataKey="captures_raton" name="Ratón" stackId="r" fill={COLORS.red} />
                                            <Bar dataKey="captures_rata" name="Rata" stackId="r" fill={COLORS.navy} />
                                        </BarChart>
                                    </ResponsiveContainer>
                                )}
                            </CardContent>
                        </Card>

                        <Card>
                            <CardHeader>
                                <CardTitle className="text-base">Distribución de capturas</CardTitle>
                            </CardHeader>
                            <CardContent>
                                {(pests?.rodent_distribution?.length || 0) === 0 ? <Empty msg="Sin capturas registradas en este periodo." /> : (
                                    <ResponsiveContainer width="100%" height={260}>
                                        <PieChart>
                                            <Pie data={pests.rodent_distribution} dataKey="count" nameKey="label" outerRadius={90} label>
                                                {pests.rodent_distribution.map((_, i) => <Cell key={i} fill={PIE_PALETTE[i % PIE_PALETTE.length]} />)}
                                            </Pie>
                                            <Tooltip />
                                        </PieChart>
                                    </ResponsiveContainer>
                                )}
                            </CardContent>
                        </Card>
                    </div>
                </TabsContent>

                {/* BAITS */}
                <TabsContent value="baits">
                    <div className="grid lg:grid-cols-3 gap-4">
                        <Card className="lg:col-span-2">
                            <CardHeader>
                                <CardTitle className="text-base">Cebos consumidos por zona</CardTitle>
                            </CardHeader>
                            <CardContent>
                                {loading ? <SkeletonChart /> : (
                                    <ResponsiveContainer width="100%" height={320}>
                                        <BarChart data={timelineData}>
                                            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                                            <XAxis dataKey="monthLabel" tick={{ fontSize: 11 }} />
                                            <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                                            <Tooltip />
                                            <Legend wrapperStyle={{ fontSize: 12 }} />
                                            <Bar dataKey="consumed_green" name="Zona Verde" stackId="b" fill={COLORS.green} />
                                            <Bar dataKey="consumed_yellow" name="Zona Amarilla" stackId="b" fill={COLORS.yellow} />
                                        </BarChart>
                                    </ResponsiveContainer>
                                )}
                            </CardContent>
                        </Card>

                        <Card>
                            <CardHeader>
                                <CardTitle className="text-base">Tipos de plaga detectados</CardTitle>
                            </CardHeader>
                            <CardContent>
                                {(pests?.pest_distribution?.length || 0) === 0 ? <Empty /> : (
                                    <ResponsiveContainer width="100%" height={260}>
                                        <PieChart>
                                            <Pie data={pests.pest_distribution} dataKey="count" nameKey="label" outerRadius={90} label>
                                                {pests.pest_distribution.map((_, i) => <Cell key={i} fill={PIE_PALETTE[i % PIE_PALETTE.length]} />)}
                                            </Pie>
                                            <Tooltip />
                                        </PieChart>
                                    </ResponsiveContainer>
                                )}
                            </CardContent>
                        </Card>
                    </div>
                </TabsContent>

                {/* UV */}
                <TabsContent value="uv">
                    <div className="grid lg:grid-cols-3 gap-4">
                        <Card className="lg:col-span-2">
                            <CardHeader>
                                <CardTitle className="text-base">Insectos capturados por mes (UV)</CardTitle>
                            </CardHeader>
                            <CardContent>
                                {loading ? <SkeletonChart /> : (
                                    <ResponsiveContainer width="100%" height={320}>
                                        <LineChart data={uvMonthly}>
                                            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                                            <XAxis dataKey="monthLabel" tick={{ fontSize: 11 }} />
                                            <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                                            <Tooltip />
                                            <Line type="monotone" dataKey="insects" name="Insectos" stroke={COLORS.purple} strokeWidth={3} dot={{ r: 4 }} />
                                        </LineChart>
                                    </ResponsiveContainer>
                                )}
                            </CardContent>
                        </Card>

                        <Card>
                            <CardHeader>
                                <CardTitle className="text-base">Especies predominantes</CardTitle>
                            </CardHeader>
                            <CardContent>
                                {(uv?.species?.length || 0) === 0 ? <Empty /> : (
                                    <ResponsiveContainer width="100%" height={260}>
                                        <PieChart>
                                            <Pie data={uv.species} dataKey="count" nameKey="label" outerRadius={90} label>
                                                {uv.species.map((_, i) => <Cell key={i} fill={PIE_PALETTE[i % PIE_PALETTE.length]} />)}
                                            </Pie>
                                            <Tooltip />
                                        </PieChart>
                                    </ResponsiveContainer>
                                )}
                                <div className="text-[11px] text-muted-foreground mt-2">
                                    Total de lámparas UV: <b>{uv?.total_lamps ?? 0}</b>
                                </div>
                            </CardContent>
                        </Card>
                    </div>
                </TabsContent>

                {/* REPORTS */}
                <TabsContent value="reports">
                    <Card>
                        <CardHeader>
                            <CardTitle className="text-base flex items-center justify-between flex-wrap gap-2">
                                <span className="flex items-center gap-2">
                                    <FileText className="w-4 h-4 text-indigo-600" />
                                    Informes ejecutivos generados
                                </span>
                                <Button size="sm" onClick={() => setDialogOpen(true)} data-testid="open-exec-report-dialog-tab"
                                        className="bg-amber-500 text-black hover:bg-amber-400">
                                    <Sparkles className="w-3.5 h-3.5 mr-1.5" /> Generar nuevo
                                </Button>
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            <ExecutiveReportsList reports={reports} />
                        </CardContent>
                    </Card>
                </TabsContent>
            </Tabs>

            <ExecutiveReportDialog
                open={dialogOpen}
                onClose={() => setDialogOpen(false)}
                floorPlans={floorPlans}
                onGenerated={() => reloadReports()}
            />
        </div>
    );
}

function SkeletonChart() {
    return (
        <div className="h-[320px] bg-slate-50 animate-pulse rounded-lg flex items-center justify-center text-xs text-muted-foreground">
            Cargando datos…
        </div>
    );
}

function Empty({ msg = "Sin datos en este periodo." }) {
    return <div className="h-[200px] flex items-center justify-center text-xs text-muted-foreground">{msg}</div>;
}
